/**
 * ESERV2-1450 — split the pre-submit stock check into what blocks and what
 * only warns.
 *
 * Heritage's ERP rejects any line whose product is not sellable at the ordered
 * branch and never reports it (CreateSalesOrder answers 00 and the order dies),
 * so `sellable: false` from /check-availability blocks the submit outright —
 * no "Submit anyway". Low stock on a sellable part stays a warning the user
 * may override: backorder is Heritage's call, not ours.
 */

export interface StockCheckRow {
	item_name?: string | null;
	heritagePartNumber?: string | null;
	sufficient?: boolean;
	sellable?: boolean;
	sellableReason?: string | null;
	message?: string | null;
}

export interface PartitionedStockResults {
	/** Lines Heritage will reject — the submit must not proceed. */
	blocking: string[];
	/** Lines short on stock — the user may submit anyway. */
	warnings: string[];
}

const lineName = (row: StockCheckRow): string =>
	row.item_name || row.heritagePartNumber || "Item";

export function partitionStockResults(
	rows: StockCheckRow[] | null | undefined,
	branchLabel: string
): PartitionedStockResults {
	const blocking: string[] = [];
	const warnings: string[] = [];
	for (const row of rows || []) {
		if (row.sellable === false) {
			blocking.push(
				`${lineName(row)} at ${branchLabel}: ${row.sellableReason || row.message || "not sellable at this branch"}`
			);
		} else if (row.sufficient === false) {
			warnings.push(`${lineName(row)} at ${branchLabel}: ${row.message || "Unavailable"}`);
		}
	}
	return { blocking, warnings };
}

/** The message shown when the submit is blocked. */
export function sellabilityBlockMessage(blocking: string[]): string {
	return `Heritage will reject this order — these lines are not sellable at the ordered branch: ${blocking.join(
		"; "
	)}. Remove or relink them (or change the branch) before submitting.`;
}

/**
 * True when the stock check failed because the user's Heritage login cannot be
 * used on this environment (API answers 409 HERITAGE_CREDENTIALS). Such a
 * failure blocks the submit — "Submit anyway" would fail on the same login.
 */
export function isHeritageCredentialsError(error: unknown): boolean {
	if (!error || typeof error !== "object") return false;
	const response = (error as { response?: { status?: number; data?: unknown } }).response;
	const data = response?.data;
	if (!data || typeof data !== "object") return false;
	return (data as { code?: unknown }).code === "HERITAGE_CREDENTIALS";
}

/** The message shown when the submit is blocked by an unusable Heritage login. */
export function credentialsBlockMessage(): string {
	return "Your Heritage login cannot be used on this environment, so the order cannot be submitted to Heritage. Open Vendors → Heritage Landscape Supply → Heritage credentials, enter the customer account id and API key again and save them, then try again.";
}
